import { PurchasedExtendedWarrantyDataSortKeys } from "@redotech/redo-model/analytics/analytics";
import {
  OrderProductColumnKey,
  ReturnDataColumnKey,
  ReturnProductColumnKey,
  WarrantyCustomerDataColumnKey,
} from "@redotech/redo-model/analytics/reports/columns";
import {
  CustomAnalyticsReportColumnSchema,
  CustomAnalyticsReportSortSchema,
  CustomAnalyticsReportTitleSchema,
} from "@redotech/redo-model/analytics/reports/custom-analytics-report";
import { CustomAnalyticsReportFiltersSchema } from "@redotech/redo-model/analytics/reports/custom-analytics-report-filters";
import { ReportFilterSchema } from "@redotech/redo-model/analytics/reports/filters/report-filter";
import {
  ReportSchema,
  ReportSchema_,
} from "@redotech/redo-model/analytics/reports/report";
import { ReportDataType } from "@redotech/redo-model/analytics/reports/report-data-type";
import { SortDirection } from "@redotech/redo-model/tables/table";
import { unaryStreamRpc } from "@redotech/rpc/definition";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";

const ReturnDataEnumFields_ = z.object({
  id: z.string().optional(),
  returnType: z.string().optional(),
  orderNumbers: z.array(z.string()).optional(),
  newOrderNumber: z.string().optional(),
  compensationMethods: z.array(z.string()).optional(),
  status: z.string().optional(),
  covered: z.boolean().optional(),
  shopifyOrderIds: z.array(z.string()).optional(),
  salesChannels: z.array(z.string()).optional(),
  giftcardCodes: z.array(z.string()).optional(),
  tags: z
    .array(z.object({ tag_name: z.string(), pill_theme: z.string() }))
    .optional(),
});

const ReturnDataStringFields_ = z.object({
  customerEmail: z.string().optional(),
  currency: z.string().optional(),
  media: z.array(z.string()).optional(),
  approvedBy: z.string().optional(),
  proccessedBy: z.string().optional(),
  variantExchangeProductIds: z.array(z.string()).optional(),
  variantExchangeTitles: z.array(z.string()).optional(),
  variantExchangeVariantIds: z.array(z.string()).optional(),
  exchangeForProductTitles: z.array(z.string()).optional(),
  exchangeForProductIds: z.array(z.string()).optional(),
  exchangeForVariantIds: z.array(z.string()).optional(),
  advancedExchangeTitles: z.array(z.string()).optional(),
  advancedExchangeVariantIds: z.array(z.string()).optional(),
  exchangeOrderIds: z.array(z.string()).optional(),
  customerComments: z.array(z.string()).optional(),
  aiReturnReasonSummaries: z.array(z.string()).optional(),
  referralEmail: z.string().optional(),
  trackingNumbers: z.array(z.string()).optional(),
  merchantAddressNames: z.array(z.string()).optional(),
  orderCountryCodes: z.array(z.string()).optional(),
  productTitles: z.array(z.string()).optional(),
  variantTitles: z.array(z.string()).optional(),
  productSkus: z.array(z.string()).optional(),
  productStatuses: z.array(z.string()).optional(),
  productReasons: z.array(z.string()).optional(),
  productIds: z.array(z.string()).optional(),
  variantIds: z.array(z.string()).optional(),
  productRejectReasons: z.array(z.string()).optional(),
  returnMethod: z.string().optional(),
  dispositionMerchantNotes: z.array(z.string()).optional(),
  dispositionMerchantGrades: z.array(z.string()).optional(),
  dispositionMerchantOutcomes: z.array(z.string()).optional(),
  customerImages: z.array(z.string()).optional(),
  fulfillmentLocationIds: z.array(z.string()).optional(),
  vendors: z.array(z.string()).optional(),
  createdBy: z.string().optional(),
  returnCreatedBy: z.string().optional(),
  shippingProvider: z.string().optional(),
  returnAssessments: z.string().optional(),
  warrantyAssessments: z.string().optional(),
});

const ReturnDataNumberFields_ = z.object({
  returnValueNoTaxNoAdjustment: z.number().optional(),
  returnTax: z.number().optional(),
  returnAdjustment: z.number().optional(),
  returnTotalValue: z.number().optional(),
  returnExchangeValue: z.number().optional(),
  returnStoreCreditValue: z.number().optional(),
  returnRefundValue: z.number().optional(),
  originalOrderItemQuantity: z.number().optional(),
  returnedProductCount: z.number().optional(),
  upsellAmount: z.number().optional(),
  productPrices: z.array(z.number()).optional(),
});

const ReturnDataDateFields_ = z.object({
  orderCreatedAts: z.array(z.string()).optional(),
  returnCreatedAt: z.date().optional(),
  returnProcessedAt: z.date().optional(),
  approvedAt: z.date().optional(),
  returnToMerchantDeliveredDate: z.date().optional(),
});

const ReturnDataSchema_ = z.object({
  enumFields: ReturnDataEnumFields_,
  stringFields: ReturnDataStringFields_,
  numberFields: ReturnDataNumberFields_,
  dateFields: ReturnDataDateFields_,
});

const ReturnProductStringFields_ = z.object({
  reason: z.string().optional(),
  primaryReason: z.string().optional(),
  secondaryReason: z.string().optional(),
  rejectReason: z.string().optional(),
  returnMethod: z.string().optional(),
  createdBy: z.string().optional(),
  customerEmail: z.string().optional(),
  dispositionMerchantNotes: z.string().optional(),
  dispositionMerchantGrade: z.string().optional(),
  dispositionMerchantOutcome: z.string().optional(),
  customerImages: z.array(z.string()).optional(),
  fulfillmentLocationId: z.string().optional(),
  customerComments: z.array(z.string()).optional(),
  aiReturnReasonSummary: z.string().optional(),
  trackingNumbers: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  vendor: z.string().optional(),
  orderCountryCode: z.string().optional(),
  shippingProvider: z.string().optional(),
  returnCreatedBy: z.string().optional(),
  returnAssessments: z.string().optional(),
  warrantyAssessments: z.string().optional(),
});

const ReturnProductNumberFields_ = z.object({
  productValueNoTaxNoAdjustment: z.number().optional(),
  productTax: z.number().optional(),
  productAdjustment: z.number().optional(),
  productTotalValue: z.number().optional(),
  productExchangeValue: z.number().optional(),
  productStoreCreditValue: z.number().optional(),
  productRefundValue: z.number().optional(),
  productPrice: z.number().optional(),
  productQuantity: z.number().optional(),
});

const ReturnProductDateFields_ = z.object({
  created_at: z.date().optional(),
  updated_at: z.date().optional(),
  return_processed_at: z.date().optional(),
  return_approved_at: z.date().optional(),
  product_processed_at: z.date().optional(),
  product_approved_at: z.date().optional(),
  returnToMerchantDeliveredDate: z.date().optional(),
  order_created_at: z.date().optional(),
});

const ReturnProductEnumFields_ = z.object({
  orderNumber: z.string().optional(),
  shopifyOrderId: z.string().optional(),
  returnId: z.string().optional(),
  returnType: z.string().optional(),
  status: z.string().optional(),
  returnStatus: z.string().optional(),
  productName: z.string().optional(),
  shopifyProductId: z.string().optional(),
  variantName: z.string().optional(),
  shopifyVariantId: z.string().optional(),
  sku: z.string().optional(),
  compensationMethod: z.string().optional(),
  processedBy: z.string().optional(),
  approvedBy: z.string().optional(),
  salesChannel: z.string().optional(),
  covered: z.boolean().optional(),
  currency: z.string().optional(),
});

const ReturnProductDataSchema_ = z.object({
  stringFields: ReturnProductStringFields_,
  numberFields: ReturnProductNumberFields_,
  dateFields: ReturnProductDateFields_,
  enumFields: ReturnProductEnumFields_,
});

const WarrantyCustomerDataSchema_ = z.object({
  teamId: z.string(),
  warrantyName: z.string(),
  customerEmail: z.string(),
  productName: z.string(),
  variantName: z.string(),
  warrantyRegistrationDate: z.union([z.string(), z.date()]),
  customerPhone: z.string().optional(),
  customerName: z.string().optional(),
  customerAddress: z.string().optional(),
  customerSecondAddress: z.string().optional(),
  customerCity: z.string().optional(),
  customerProvince: z.string().optional(),
  customerPostal: z.string().optional(),
  optedIntoMarketing: z.boolean().optional(),
  source: z.string().optional(),
  orderNumber: z.string().optional(),
});

const OrderProductEnumFields_ = z.object({
  orderName: z.string().optional(),
  shopifyOrderId: z.string().optional(),
  lineItemId: z.string().optional(),
  productId: z.string().optional(),
  variantId: z.string().optional(),
  productTitle: z.string().optional(),
  variantTitle: z.string().optional(),
  sku: z.string().optional(),
  productType: z.string().optional(),
  vendor: z.string().optional(),
  salesChannel: z.string().optional(),
  customerEmail: z.string().optional(),
  customerName: z.string().optional(),
  orderCountryCode: z.string().optional(),
  financialStatus: z.string().optional(),
  fulfillmentLocationId: z.string().optional(),
  fulfillmentShipmentStatus: z.string().optional(),
  currency: z.string().optional(),
});

const OrderProductStringFields_ = z.object({
  upc: z.string().optional(),
  tags: z.array(z.string()).optional(),
  orderTrackingNumbers: z.array(z.string()).optional(),
  returnIds: z.array(z.string()).optional(),
});

const OrderProductBooleanFields_ = z.object({
  isExchangeOrder: z.boolean().optional(),
  isProtected: z.boolean().optional(),
  isRedoEligible: z.boolean().optional(),
  isPackageProtected: z.boolean().optional(),
});

const OrderProductNumberFields_ = z.object({
  quantity: z.number().optional(),
  unitPrice: z.number().optional(),
  price: z.number().optional(),
  discountAllocations: z.number().optional(),
  taxPrice: z.number().optional(),
  totalQuantityReturned: z.number().optional(),
  totalReturnRefundValue: z.number().optional(),
  totalReturnStoreCreditValue: z.number().optional(),
  totalReturnExchangeValue: z.number().optional(),
  totalReturnTax: z.number().optional(),
  totalReturnProductValue: z.number().optional(),
  totalReturnValueNoTaxNoAdjustment: z.number().optional(),
});

const OrderProductDateFields_ = z.object({
  orderProcessedDate: z.date().optional(),
  orderItemDeliveredAt: z.date().optional(),
});

const OrderProductDataSchema_ = z.object({
  enumFields: OrderProductEnumFields_,
  stringFields: OrderProductStringFields_,
  booleanFields: OrderProductBooleanFields_,
  numberFields: OrderProductNumberFields_,
  dateFields: OrderProductDateFields_,
});

const PurchasedExtendedWarrantyDataSchema_ = z.object({
  orderId: z.string().optional(),
  companyName: z.string().optional(),
  name: z.string().optional(),
  email: z.string().optional(),
  address1: z.string().optional(),
  address2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  zip: z.string().optional(),
  phoneNumber: z.string().optional(),
  orderDate: z.date().optional(),
  deliveryDate: z.date().optional(),
  itemPrice: z.number().optional(),
  itemSku: z.string().optional(),
  itemName: z.string().optional(),
  warrantyPrice: z.number().optional(),
  warrantyPurchaseDate: z.date().optional(),
  termPurchased: z.string().optional(),
  activationDate: z.date().optional(),
  expirationDate: z.date().optional(),
  warrantyUnitPrice: z.number().optional(),
  warrantyQuantity: z.number().optional(),
  revSharePrice: z.number().optional(),
  shopCurrency: z.string().optional(),
});

const CustomAnalyticsReportStatusSchema = z.enum([
  "preparing",
  "ready",
  "failed",
]);

const CustomAnalyticsReportSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  title: z.string(),
  storedTitle: z.string().nullable(),
  requestedQuestion: z.string(),
  reportSpec: z.string().nullable(),
  explanation: z.string(),
  status: CustomAnalyticsReportStatusSchema,
  columnsMetadata: z.array(CustomAnalyticsReportColumnSchema).nullable(),
  defaultSort: CustomAnalyticsReportSortSchema.nullable(),
  errorMessage: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  createdByUserId: z.string().nullable(),
  createdByName: z.string().nullable(),
  createdByEmail: z.string().nullable(),
});

const CustomAnalyticsReportRevisionSummarySchema = z.object({
  id: z.string(),
  revisionNumber: z.string(),
  title: z.string(),
  createdAt: z.date(),
  createdByUserId: z.string().nullable(),
  createdByName: z.string().nullable(),
  createdByEmail: z.string().nullable(),
  columnCount: z.number().int().nonnegative(),
  changes: z.array(z.string()),
});

const CustomAnalyticsReportUserFilterOptionSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  email: z.string().nullable(),
});
const CustomAnalyticsReportRowSchema = z.record(z.string(), z.unknown());

export type CustomAnalyticsReportStatus = z.infer<
  typeof CustomAnalyticsReportStatusSchema
>;
export type CustomAnalyticsReportSummary = z.infer<
  typeof CustomAnalyticsReportSummarySchema
>;
export type CustomAnalyticsReportRevisionSummary = z.infer<
  typeof CustomAnalyticsReportRevisionSummarySchema
>;
export type CustomAnalyticsReportRow = z.infer<
  typeof CustomAnalyticsReportRowSchema
>;
export type CustomAnalyticsReportUserFilterOption = z.infer<
  typeof CustomAnalyticsReportUserFilterOptionSchema
>;

export const getReport = {
  name: "getReport" as const,
  input: z.object({ _id: z.string() }),
  output: ReportSchema.nullable(),
};

export const getReports = {
  name: "getReports" as const,
  input: z.object({ dataType: createEnum(ReportDataType).optional() }),
  output: z.array(ReportSchema),
};

export const listCustomAnalyticsReports = {
  name: "listCustomAnalyticsReports" as const,
  input: z.object({
    cursor: z.string().optional(),
    limit: z.number().int().min(1).max(100).default(25),
    search: z.string().trim().max(200).optional(),
    createdByUserId: z.string().optional(),
    createdAtStart: z.date().optional(),
    createdAtEnd: z.date().optional(),
  }),
  output: z.object({
    reports: z.array(CustomAnalyticsReportSummarySchema),
    nextCursor: z.string().nullable(),
  }),
};

export const getCustomAnalyticsReportFilterOptions = {
  name: "getCustomAnalyticsReportFilterOptions" as const,
  input: z.object({}),
  output: z.object({
    users: z.array(CustomAnalyticsReportUserFilterOptionSchema),
  }),
};

export const getCustomAnalyticsReport = {
  name: "getCustomAnalyticsReport" as const,
  input: z.object({ id: z.string() }),
  output: CustomAnalyticsReportSummarySchema,
};

export const updateCustomAnalyticsReportTitle = {
  name: "updateCustomAnalyticsReportTitle" as const,
  input: z.object({ id: z.string(), title: CustomAnalyticsReportTitleSchema }),
  output: CustomAnalyticsReportSummarySchema,
};

export const listCustomAnalyticsReportRevisions = {
  name: "listCustomAnalyticsReportRevisions" as const,
  input: z.object({ id: z.string() }),
  output: z.object({
    revisions: z.array(CustomAnalyticsReportRevisionSummarySchema),
  }),
};

export const restoreCustomAnalyticsReportRevision = {
  name: "restoreCustomAnalyticsReportRevision" as const,
  input: z.object({ id: z.string(), revisionId: z.string() }),
  output: CustomAnalyticsReportSummarySchema,
};

export const getCustomAnalyticsReportRows = {
  name: "getCustomAnalyticsReportRows" as const,
  input: z.object({
    id: z.string(),
    offset: z.number().int().min(0).default(0),
    limit: z.number().int().min(1).max(100).default(25),
    sort: CustomAnalyticsReportSortSchema.nullable().optional(),
    filters: CustomAnalyticsReportFiltersSchema,
  }),
  output: z.object({
    report: CustomAnalyticsReportSummarySchema,
    columns: z.array(CustomAnalyticsReportColumnSchema),
    rows: z.array(CustomAnalyticsReportRowSchema),
    nextOffset: z.number().nullable(),
  }),
};

export const deleteCustomAnalyticsReport = {
  name: "deleteCustomAnalyticsReport" as const,
  input: z.object({ id: z.string() }),
  output: z.object({}),
};

export const regenerateCustomAnalyticsReport = {
  name: "regenerateCustomAnalyticsReport" as const,
  input: z.object({ id: z.string() }),
  output: CustomAnalyticsReportSummarySchema,
};

export const streamCustomAnalyticsReportData = unaryStreamRpc({
  name: "streamCustomAnalyticsReportData",
  input: z.object({
    id: z.string(),
    sort: CustomAnalyticsReportSortSchema.nullable().optional(),
    filters: CustomAnalyticsReportFiltersSchema,
  }),
  output: CustomAnalyticsReportRowSchema,
});

export const createReport = {
  name: "createReport" as const,
  input: ReportSchema_.omit({
    _id: true,
    team: true, // The team is set to the current team
    createdAt: true,
    updatedAt: true,
  }),
  output: ReportSchema,
};

export const updateReport = {
  name: "updateReport" as const,
  input: ReportSchema_.omit({
    team: true, // The team is set to the current team
    createdAt: true,
    updatedAt: true,
  }),
  output: ReportSchema,
};

export const deleteReport = {
  name: "deleteReport" as const,
  input: z.object({ _id: z.string() }),
  output: z.object({}),
};

export const streamReturnedLineItemsReportData = unaryStreamRpc({
  name: "streamReturnedLineItemsReportData",
  input: z.object({
    sortKey: createEnum(ReturnProductColumnKey).optional(),
    sortDirection: createEnum(SortDirection).optional(),
    filters: z.array(ReportFilterSchema),
  }),
  output: ReturnProductDataSchema_,
});

export const streamReturnsReportData = unaryStreamRpc({
  name: "streamReturnsReportData",
  input: z.object({
    sortKey: createEnum(ReturnDataColumnKey).optional(),
    sortDirection: createEnum(SortDirection).optional(),
    filters: z.array(ReportFilterSchema),
  }),
  output: ReturnDataSchema_,
});

export const streamOrderedLineItemsReportData = unaryStreamRpc({
  name: "streamOrderedLineItemsReportData",
  input: z.object({
    sortKey: createEnum(OrderProductColumnKey).optional(),
    sortDirection: createEnum(SortDirection).optional(),
    filters: z.array(ReportFilterSchema),
  }),
  output: OrderProductDataSchema_,
});

export const streamWarrantyCustomersReportData = unaryStreamRpc({
  name: "streamWarrantyCustomersReportData",
  input: z.object({
    sortKey: createEnum(WarrantyCustomerDataColumnKey).optional(),
    sortDirection: createEnum(SortDirection).optional(),
    filters: z.array(ReportFilterSchema),
  }),
  output: WarrantyCustomerDataSchema_,
});

export const streamPurchasedExtendedWarrantyReportData = unaryStreamRpc({
  name: "streamPurchasedExtendedWarrantyReportData",
  input: z.object({
    sortKey: createEnum(PurchasedExtendedWarrantyDataSortKeys).optional(),
    sortDirection: createEnum(SortDirection).optional(),
    filters: z.array(ReportFilterSchema),
  }),
  output: PurchasedExtendedWarrantyDataSchema_,
});
